import { useRef, useState, useCallback } from "react";

const DURATION = 40; // seconds

export const AnnouncementMarquee = ({ announcements }) => {
  const contentRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartX = useRef(0);
  const dragStartTranslateX = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const getComputedTranslateX = () => {
    const el = contentRef.current;
    if (!el) return 0;
    const transform = window.getComputedStyle(el).transform;
    if (!transform || transform === "none") return 0;
    const match = transform.match(/matrix.*\((.+)\)/);
    if (!match) return 0;
    return parseFloat(match[1].split(", ")[4]) || 0;
  };

  const handleDragStart = useCallback((clientX) => {
    const el = contentRef.current;
    if (!el) return;
    // Snapshot the current animated position and freeze it
    const currentX = getComputedTranslateX();
    dragStartTranslateX.current = currentX;
    dragStartX.current = clientX;
    el.style.animation = "none";
    el.style.transform = `translateX(${currentX}px)`;
    isDraggingRef.current = true;
    setIsDragging(true);
  }, []);

  const handleDragMove = useCallback((clientX) => {
    if (!isDraggingRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    const contentWidth = el.scrollWidth / 2;
    let newX = dragStartTranslateX.current + (clientX - dragStartX.current);
    // Wrap to keep within [-contentWidth, 0)
    while (newX <= -contentWidth) newX += contentWidth;
    while (newX > 0) newX -= contentWidth;
    el.style.transform = `translateX(${newX}px)`;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    const el = contentRef.current;
    if (!el) return;
    // Resume CSS animation from current drag position
    const match = el.style.transform.match(/translateX\((-?[\d.]+)px\)/);
    const currentX = match ? parseFloat(match[1]) : 0;
    const contentWidth = el.scrollWidth / 2;
    const progress = Math.abs(currentX) / contentWidth;
    el.style.transform = "";
    el.style.animation = `marquee-scroll ${DURATION}s linear infinite`;
    el.style.animationDelay = `${-(progress * DURATION)}s`;
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  if (!announcements || !announcements.length) return null;

  const duplicatedAnnouncements = [...announcements, ...announcements];

  return (
    <div
      style={{
        overflow: "hidden",
        width: "100%",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "pan-y",
      }}
      onMouseDown={(e) => { e.preventDefault(); handleDragStart(e.clientX); }}
      onMouseMove={(e) => handleDragMove(e.clientX)}
      onMouseUp={handleDragEnd}
      onMouseLeave={handleDragEnd}
      onTouchStart={(e) => handleDragStart(e.touches[0].clientX)}
      onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
      onTouchEnd={handleDragEnd}
    >
      <div
        ref={contentRef}
        style={{
          display: "inline-flex",
          whiteSpace: "nowrap",
          animation: `marquee-scroll ${DURATION}s linear infinite`,
        }}
      >
        {duplicatedAnnouncements.map((announcement, index) => (
          <AnnouncementItem key={index} title={announcement.title} />
        ))}
      </div>
    </div>
  );
};

const AnnouncementItem = ({ title }) => (
  <span style={{ marginRight: "20px", padding: "0 10px" }}>
    {title} &nbsp; * &nbsp;
  </span>
);
